import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import config from '../config';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

function generateBatchId(product, manufacturer) {
  const prefix = manufacturer
    ? manufacturer.substring(0, 3).toUpperCase().replace(/\s/g, '')
    : 'VRC';
  const date = new Date();
  const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const random = Math.floor(Math.random() * 9000 + 1000);
  return `${prefix}-${dateStr}-${random}`;
}

function Dashboard() {
  const [stats, setStats] = useState({
    totalBatches: 0,
    activeBatches: 0,
    recalledBatches: 0,
    totalScans: 0,
    pendingRecalls: 0,
    expiringSoon: 0,
    expired: 0,
  });
  const [recentBatches, setRecentBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const username = localStorage.getItem('verichain_username') || '';
  const name = localStorage.getItem('verichain_user') || '';
  const role = localStorage.getItem('verichain_role') || 'operator';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/stats`);
      const s = response.data.stats;
      setStats({
        totalBatches: s.totalBatches || 0,
        activeBatches: s.activeBatches || 0,
        recalledBatches: s.recalledBatches || 0,
        totalScans: s.totalScans || 0,
        pendingRecalls: s.pendingRecalls || 0,
        expiringSoon: s.expiringSoon || 0,
        expired: s.expired || 0,
      });

      const batchResponse = await axios.get(`${config.API_URL}/batches`);
      const batches = batchResponse.data.batches || [];
      setRecentBatches(batches.slice(0, 5));

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'recalled') return '#C0392B';
    if (status === 'pending') return '#F0B429';
    return '#22A860';
  };

  const getStatusBg = (status) => {
    if (status === 'recalled') return '#2D1515';
    if (status === 'pending') return '#2D1F00';
    return '#0D2A1A';
  };

  return (
    <div className="page">
      <Navbar />

      <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFFFFF' }}>
              Dashboard
            </h1>
            <p style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
              Welcome back, <span style={{ color: '#F0B429' }}>{name}</span> · {username} · {role}
            </p>
          </div>
          <div style={{
            backgroundColor: '#0B1D35',
            border: '1px solid #1E3A6E',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '12px',
            color: '#64748B',
          }}>
            {new Date().toLocaleDateString('en-ZA', { dateStyle: 'full' })}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '3px solid #1E3A6E',
              borderTop: '3px solid #F0B429',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: '#64748B', fontSize: '13px' }}>Loading dashboard...</p>
          </div>
        ) : (
          <>
            {/* Top row: Donut chart + Total Scans card */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '16px',
              marginBottom: '16px',
            }}>
              {/* Batch Status Donut Chart */}
              <div style={{
                backgroundColor: '#1a2a43',
                border: '1px solid #1E3A6E',
                borderRadius: '8px',
                padding: '20px',
                borderTop: '3px solid #F0B429',
              }}>
                <p style={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>
                  Batch Status
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: stats.activeBatches },
                        { name: 'Recalled', value: stats.recalledBatches },
                        { name: 'Pending', value: stats.pendingRecalls },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                    >
                      <Cell fill="#22A860" />
                      <Cell fill="#C0392B" />
                      <Cell fill="#F0B429" />
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0B1D35', border: '1px solid #1E3A6E', borderRadius: '6px', fontSize: '12px' }}
                      itemStyle={{ color: '#FFFFFF' }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: '12px', color: '#94a3b8' }}
                    />
                    {/* Center label */}
                    <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fill: '#FFFFFF', fontSize: '28px', fontWeight: 'bold' }}>
                      {stats.totalBatches}
                    </text>
                    <text x="50%" y="57%" textAnchor="middle" dominantBaseline="middle" style={{ fill: '#64748B', fontSize: '10px', textTransform: 'uppercase' }}>
                      Total
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Total Scans card */}
              <div style={{
                backgroundColor: '#1a2a43',
                border: '1px solid #1E3A6E',
                borderRadius: '8px',
                padding: '20px',
                borderTop: '3px solid #0EA5E9',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                background: 'linear-gradient(160deg, #1a2a43 60%, #0a1f38 100%)',
              }}>
                <p style={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>
                  Total Scans
                </p>
                <p style={{ fontSize: '56px', fontWeight: 'bold', color: '#0EA5E9', lineHeight: 1 }}>
                  {stats.totalScans.toLocaleString()}
                </p>
                <p style={{ fontSize: '11px', color: '#64748B', marginTop: '12px' }}>
                  QR scans across all batches
                </p>
              </div>
            </div>

            {/* Alert Metrics Horizontal Bar Chart */}
            <div style={{
              backgroundColor: '#1a2a43',
              border: '1px solid #1E3A6E',
              borderRadius: '8px',
              padding: '20px',
              borderTop: '3px solid #C0392B',
              marginBottom: '32px',
            }}>
              <p style={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>
                Alert Metrics
              </p>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart
                  layout="vertical"
                  data={[
                    { name: 'Pending Approvals', value: stats.pendingRecalls, color: '#C0392B' },
                    { name: 'Expiring Soon', value: stats.expiringSoon, color: '#F0B429' },
                    { name: 'Expired Batches', value: stats.expired, color: '#64748b' },
                  ]}
                  margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B1D35', border: '1px solid #1E3A6E', borderRadius: '6px', fontSize: '12px' }}
                    itemStyle={{ color: '#FFFFFF' }}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24}>
                    {[
                      { color: '#C0392B' },
                      { color: '#F0B429' },
                      { color: '#64748b' },
                    ].map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent Batches */}
            <div className="card">
              <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#F0B429' }}>
                Recent Batches
              </h2>

              {recentBatches.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px' }}>
                  <p style={{ color: '#64748B', fontSize: '13px' }}>No batches registered yet.</p>
                  <p style={{ color: '#64748B', fontSize: '12px', marginTop: '8px' }}>Start by registering your first batch.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Batch ID', 'Product', 'Quantity', 'Expiry Date', 'Scans', 'Status', 'Registered'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left',
                          padding: '8px',
                          fontSize: '11px',
                          color: '#64748B',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          borderBottom: '1px solid #1E3A6E',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentBatches.map((batch, i) => {
                      const isExpired = batch.expiryDate && new Date(batch.expiryDate) < new Date();
                      const isExpiringSoon = batch.expiryDate && !isExpired && new Date(batch.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #1E3A6E' }}>
                          <td style={{ padding: '12px 8px', fontSize: '13px', color: '#F0B429' }}>
                            {batch.batchId}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '13px', color: '#FFFFFF' }}>
                            {batch.product}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '13px', color: '#FFFFFF' }}>
                            {batch.quantity}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '13px' }}>
                            <span style={{
                              color: isExpired ? '#C0392B' : isExpiringSoon ? '#F0B429' : '#64748B',
                              fontWeight: isExpired || isExpiringSoon ? 'bold' : 'normal',
                            }}>
                              {batch.expiryDate || 'N/A'}
                              {isExpired && ' EXPIRED'}
                              {isExpiringSoon && ' EXPIRING SOON'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '13px', color: '#0EA5E9' }}>
                            {batch.scanCount || 0}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span style={{
                              backgroundColor: getStatusBg(batch.status),
                              border: `1px solid ${getStatusColor(batch.status)}`,
                              color: getStatusColor(batch.status),
                              padding: '3px 10px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}>
                              {batch.status || 'active'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '13px', color: '#64748B' }}>
                            {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString('en-ZA', {
                              dateStyle: 'medium'
                            }) : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export { generateBatchId };
export default Dashboard;